import { useEffect, useState } from "react";
import axios from "axios";
import { QrCode, Loader2, ArrowRight } from "lucide-react";
import { API } from "../lib/api";
import api from "../lib/api";

// The ONE control for "QR code on the bottom", used by the download modal, the Step 3 print profile, the share page
// download and the order options. Checked by default (the backend default is ON); unchecking changes only this
// export/order unless "remember for this duck" is used. When the base is too small the checkbox is shown as
// unavailable with the backend's own wording and a one-click jump to the first size that fits.
export function useQrDecision({ creationId, height, owner = true, qr = null }) {
  const [decision, setDecision] = useState(null);

  useEffect(() => {
    let alive = true;
    setDecision(null);
    const params = { height, ...(qr === null || qr === undefined ? {} : { qr: qr ? 1 : 0 }) };
    const req = owner
      ? api.get(`/creations/${creationId}/qr-bottom`, { params })
      : axios.get(`${API}/share/${creationId}/qr-bottom`, { params });
    req.then(({ data }) => alive && setDecision(data)).catch(() => alive && setDecision(null));
    return () => {
      alive = false;
    };
  }, [creationId, height, owner, qr]);

  return decision;
}

export const QrBottomChoice = ({ decision, checked, onChange, onRemember, onSwitchHeight, remembered, testid = "qr-choice" }) => {
  const anyFit = (decision?.heights_fit || []).length > 0;
  // "unavailable" means the QR was wanted but cannot be printed (state "skipped"). A plain unchecked box
  // (state "off") must stay a normal, re-checkable choice — the backend does not measure the base then.
  const unavailable = decision?.state === "skipped";
  const on = unavailable ? false : checked !== false;

  return (
    <div data-testid={testid} className="rounded-2xl border border-border bg-card/60 px-4 py-3">
      <label className="flex items-start gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          data-testid={`${testid}-checkbox`}
          checked={on}
          disabled={unavailable || !decision}
          onChange={(e) => onChange?.(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-[color:var(--accent)] disabled:opacity-40"
        />
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2 text-sm font-semibold">
            {decision ? <QrCode className={`h-4 w-4 shrink-0 ${on ? "text-accent" : "text-muted-foreground"}`} /> : <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />}
            QR code on the bottom
            {unavailable && <span data-testid={`${testid}-unavailable`} className="text-xs font-normal text-muted-foreground">· unavailable</span>}
          </span>
          <span data-testid={`${testid}-note`} className="block text-xs text-muted-foreground mt-0.5">
            {!decision
              ? "Checking this duck's base…"
              : unavailable
                ? decision.notice
                : `unchecked = plain flat bottom${decision.module_mm ? ` · ${decision.module_mm} mm modules at ${decision.height_mm} mm` : ""}`}
          </span>
        </span>
      </label>

      {unavailable && anyFit && decision.heights_fit.some((h) => h !== decision.height_mm) && onSwitchHeight && (
        <button
          type="button"
          data-testid={`${testid}-switch-size`}
          onClick={() => onSwitchHeight(decision.suggest_height || decision.heights_fit[0])}
          className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-accent/15 text-accent px-3 py-1.5 text-xs font-bold hover:bg-accent/25"
        >
          Switch to {decision.suggest_height || decision.heights_fit[0]} mm <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}

      {onRemember && !unavailable && (
        <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid={`${testid}-remember`}
            checked={!!remembered}
            onChange={(e) => onRemember(e.target.checked)}
            className="h-3.5 w-3.5 accent-[color:var(--accent)]"
          />
          Remember for this duck
        </label>
      )}
    </div>
  );
};
